// MACHINERY V2 (Stroje) — the eight SR form fields, in artboard order (M2.6).
//
// Spec: data package §7 FIELD TABLE, top to bottom and in THIS order:
//   Názov hlásenia · Kód alarmu · Urgencia · Dátum + čas · Dotknutá súčasť ·
//   Popis · Technické poznámky · Prílohy
// Reference impl: design_handoff_machinery_v3/machinery-sr.jsx `SrCreateScreen`
// (lines 552-597). Artboards 0702 (Refined Claude) / 0703 (MachineID Native) —
// ONE component for both palettes.
//
// VALIDATION IS §7, NOT A REDESIGN:
//   title    required + RHF maxLength 100 + HTML maxLength 100 (backend
//            [MaxLength(100)] on ChangeServiceReportViewModel.Title — read in
//            the server worktree, ChangeServiceReportViewModel.cs:7-8).
//   alarmCode          RHF + HTML maxLength 100  (server :13-14).
//   urgency            1 / 2 / 4 ONLY. There is NO 3 and no "medium" key —
//                      `priorities.urgent | high | low` (§7, §13).
//   openDate           may not be in the future; the two controls bind the SAME
//                      field. Quirk preserved — see srDateTime.ts.
//   affectedPart       UI-REQUIRED although the DTO is nullable (server :9,
//                      `Guid? AffectedPartCategoryId`). v1 makes the same call;
//                      keep it.
//   description        native maxLength 2000, CLIENT-ONLY — the server column
//                      is unbounded (no [MaxLength] on Description, server :10).
//   notes              native maxLength 4000 (server :11-12).
//
// The affected-part select re-validates on change, reproducing v1's
// `setTimeout(() => trigger("affectedPartCategoryId"))`
// (CreateServiceReportPage.tsx:704) — without the setTimeout, which existed to
// escape MUI Autocomplete's own change cycle and has no counterpart here.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { ChangeServiceReport, ServiceReportFile } from "../../models/ServiceReportsModel";
import FormField, { FormError } from "../forms/FormField";
import FormSelect, { FormSelectOption } from "../forms/FormSelect";
import {
  AlertGlyph,
  BangGlyph,
  DocGlyph,
  NoteGlyph,
  PaperclipGlyph,
  WrenchGlyph,
} from "../forms/FormGlyphs";
import SrAttachmentsField from "./SrAttachmentsField";
import SrDateTimeField from "./SrDateTimeField";
import { SrPartOption } from "./srMachineParts";

/** §7 / §13: values 1, 2 and 4. Colours are v1's dot palette
 *  (CreateServiceReportPage.tsx:563 / :569 / :575). */
const URGENCY_ROWS = [
  { id: "1", labelKey: "myMachinery:priorities.urgent", dotColor: "#C0392B" },
  { id: "2", labelKey: "myMachinery:priorities.high", dotColor: "#E67E22" },
  { id: "4", labelKey: "myMachinery:priorities.low", dotColor: "#27AE60" },
];

export interface SrCreateFieldsProps {
  form: UseFormReturn<ChangeServiceReport>;
  partOptions: SrPartOption[];
  files: ServiceReportFile[];
  fileError?: string;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
}

const SrCreateFields: React.FC<SrCreateFieldsProps> = ({
  form,
  partOptions,
  files,
  fileError,
  onAddFiles,
  onRemoveFile,
}) => {
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = form;

  const urgencyOptions: FormSelectOption[] = URGENCY_ROWS.map((r) => ({
    id: r.id,
    label: translate(r.labelKey),
    dotColor: r.dotColor,
  }));

  const partSelectOptions: FormSelectOption[] = partOptions.map((o) => ({
    id: o.id,
    label: o.label,
    group: o.group,
    // A fallback row has no categoryId and must never be chosen — see
    // srMachineParts.ts. An empty id is the marker.
    disabled: !o.selectable,
  }));

  return (
    <>
      {/* 1 — NÁZOV HLÁSENIA */}
      <FormField
        glyph={<DocGlyph />}
        label={translate("myMachinery:reportTitleSection")}
        required
        htmlFor="sr-title"
        error={errors.title?.message}
      >
        <input
          id="sr-title"
          className={"mv2-input" + (errors.title ? " is-invalid" : "")}
          style={{ fontFamily: "var(--font-mono)" }}
          placeholder={translate("myMachinery:reportTitle")}
          title={translate("myMachinery:titleCharLimit")}
          maxLength={100}
          aria-invalid={!!errors.title}
          aria-describedby={errors.title ? "sr-title-error" : undefined}
          {...register("title", {
            required: translate("common:forms.required"),
            maxLength: { value: 100, message: translate("myMachinery:titleTooLong") },
          })}
        />
      </FormField>

      {/* 2 — KÓD ALARMU */}
      <FormField
        glyph={<AlertGlyph />}
        label={translate("myMachinery:alarmCodeSection")}
        htmlFor="sr-alarm"
        error={errors.alarmCode?.message}
      >
        <input
          id="sr-alarm"
          className={"mv2-input" + (errors.alarmCode ? " is-invalid" : "")}
          style={{ fontFamily: "var(--font-mono)" }}
          placeholder={translate("myMachinery:alarmCodePlaceholder")}
          maxLength={100}
          aria-invalid={!!errors.alarmCode}
          {...register("alarmCode", {
            maxLength: { value: 100, message: translate("myMachinery:alarmCodeTooLong") },
          })}
        />
      </FormField>

      {/* 3 — URGENCIA */}
      <FormField glyph={<BangGlyph />} label={translate("myMachinery:urgencySection")}>
        <Controller
          control={control}
          name="urgency"
          render={({ field }) => (
            <FormSelect
              ariaLabel={translate("myMachinery:urgency")}
              placeholder={translate("myMachinery:urgencyNone")}
              value={field.value === undefined || field.value === null ? undefined : String(field.value)}
              options={urgencyOptions}
              clearLabel={translate("myMachinery:urgencyNone")}
              // v1: `e.target.value === "" ? undefined : Number(...)` (:541).
              onChange={(v) => field.onChange(v === undefined ? undefined : Number(v))}
            />
          )}
        />
      </FormField>

      {/* 4 — DÁTUM A ČAS (both controls bind the same `openDate`) */}
      <SrDateTimeField control={control} error={errors.openDate?.message} />

      {/* 5 — DOTKNUTÁ SÚČASŤ */}
      <FormField
        glyph={<WrenchGlyph />}
        label={translate("myMachinery:affectedPartSection")}
        required
      >
        <Controller
          control={control}
          name="affectedPartCategoryId"
          rules={{ required: translate("common:forms.required") }}
          render={({ field }) => (
            <FormSelect
              ariaLabel={translate("myMachinery:affectedMachinePart")}
              placeholder={translate("myMachinery:affectedMachinePart")}
              value={field.value || undefined}
              options={partSelectOptions}
              invalid={!!errors.affectedPartCategoryId}
              onChange={(v) => {
                // v1 writes '' when the value is cleared (:703) — kept, because
                // the required rule treats '' as missing and a Guid field must
                // never be SENT as ''. The submit path only runs on a valid form.
                field.onChange(v || "");
                trigger("affectedPartCategoryId");
              }}
            />
          )}
        />
        <FormError message={errors.affectedPartCategoryId?.message} />
      </FormField>

      {/* 6 — POPIS */}
      <FormField
        glyph={<DocGlyph />}
        label={translate("myMachinery:descriptionSection")}
        htmlFor="sr-description"
      >
        <textarea
          id="sr-description"
          className="mv2-input mv2-textarea"
          rows={4}
          maxLength={2000}
          placeholder={translate("myMachinery:descriptionPlaceholder")}
          {...register("description")}
        />
      </FormField>

      {/* 7 — TECHNICKÉ POZNÁMKY */}
      <FormField
        glyph={<NoteGlyph />}
        label={translate("myMachinery:technicalNotesSection")}
        htmlFor="sr-notes"
      >
        <textarea
          id="sr-notes"
          className="mv2-input mv2-textarea"
          rows={4}
          maxLength={4000}
          placeholder={translate("myMachinery:technicalNotesPlaceholder")}
          {...register("notes")}
        />
      </FormField>

      {/* 8 — PRÍLOHY */}
      <FormField glyph={<PaperclipGlyph />} label={translate("myMachinery:attachmentsSection")}>
        <SrAttachmentsField
          files={files}
          fileError={fileError}
          onAdd={onAddFiles}
          onRemove={onRemoveFile}
        />
      </FormField>
    </>
  );
};

export default SrCreateFields;
